import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  DownloadLeaderboardExcelRequest,
  DownloadLeaderboardExcelResponse,
  ViewLeaderboardRequest,
  ViewLeaderboardResponse
} from './leaderboard.types';

import { DOWNLOADING_LEADERBOARD_EXCEL, RETRIEVING_LEADERBOARD } from './leaderboard.constants';

import { requestDownloadLeaderboardExcel, requestViewLeaderboard } from './leaderboard.actions';

import { getDownloadLeaderboardExcelRequest, getViewLeaderboardRequest } from './leaderboard.endpoints';

import { setLeaderboard } from '../../slices/programme/accountability/accountability.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performViewLeaderboardRequest({ payload }: SagaPayload<ViewLeaderboardRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_LEADERBOARD));

    const [endpoint, options] = getViewLeaderboardRequest(requestData);

    const { data }: AxiosResponse<ViewLeaderboardResponse> = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { message, ...leaderboardData } = data;
    yield put(setLeaderboard(leaderboardData));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(setLeaderboard(undefined));
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_LEADERBOARD));
  }
}

export function* watchForViewLeaderboardRequest() {
  yield takeLatest(requestViewLeaderboard.type, performViewLeaderboardRequest);
}

export function* performDownloadLeaderboardExcelRequest({
  payload
}: SagaPayload<DownloadLeaderboardExcelRequest, string>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_LEADERBOARD_EXCEL));

    const [endpoint, options] = getDownloadLeaderboardExcelRequest(requestData);

    const { data }: AxiosResponse<DownloadLeaderboardExcelResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.base64EncodedString);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_LEADERBOARD_EXCEL));
  }
}

export function* watchForDownloadLeaderboardExcelRequest() {
  yield takeLatest(requestDownloadLeaderboardExcel.type, performDownloadLeaderboardExcelRequest);
}

export default function* leaderboardSaga() {
  yield all([watchForViewLeaderboardRequest(), watchForDownloadLeaderboardExcelRequest()]);
}
